<script setup>
// Imports
import OmniSelect from 'components/UI/OmniSelect.vue'
import ContactsPage from 'pages/customer/ContactsPage.vue'
import { format } from 'quasar'

// Constants
const { capitalize, humanStorageSize } = format
const $q = useQuasar()
const api = useFeathers()
const route = useRoute()
const itemsService = 'domains'
const router = useRouter()
const { t, d } = useI18n({ useScope: 'global' })

// Models
const useOwnerContactForAll = defineModel('useOwnerContactForAll', { type: [Boolean], required: false })
const ownerContact = defineModel('ownerContact', { type: [String, null], required: false })
const technicalContact = defineModel('technicalContact', { type: [String, null], required: false })
const administrativeContact = defineModel('administrativeContact', { type: [String, null], required: false })
const billingContact = defineModel('billingContact', { type: [String, null], required: false })

// Component props
const componentProps = defineProps({
  product: {
    type: [Object, null],
    required: true,
    default: null,
  },
})

// Stores
const authStore = useAuthStore()

// Refs
const contactsPage = ref()
const domainValidationErrors = ref({})
const validationErrors = ref({})
const pageLoading = ref(false)
const { selectedCustomerInfo } = storeToRefs(authStore)
const availableContactsOptions = ref([])

// Computed properties

// Methods
const getFilters = async (only = null) => {
  const filtersQuery = {
    only: ['contacts'],
    contacts: {
      query: {
        customer: selectedCustomerInfo.value._id,
      },
    },
  }

  if (only) {
    filtersQuery.only = only
  }

  const filters = await api.service(itemsService).filters(filtersQuery)

  if (filters.contacts) {
    availableContactsOptions.value = filters.contacts
  }

  if (ownerContact.value && !availableContactsOptions.value.find((contact) => contact.value === ownerContact.value)) {
    ownerContact.value = null
  }
  if (technicalContact.value && !availableContactsOptions.value.find((contact) => contact.value === technicalContact.value)) {
    technicalContact.value = null
  }
  if (administrativeContact.value && !availableContactsOptions.value.find((contact) => contact.value === administrativeContact.value)) {
    administrativeContact.value = null
  }
  if (billingContact.value && !availableContactsOptions.value.find((contact) => contact.value === billingContact.value)) {
    billingContact.value = null
  }
}
// Watchers
watch(
  () => contactsPage?.value?.items,
  () => {
    if (availableContactsOptions.value.length === 0) {
      return
    }

    const availableContactMap = availableContactsOptions.value.map((contact) => contact.value)

    if (!availableContactMap.includes(ownerContact.value)) {
      ownerContact.value = null
    }
    if (!availableContactMap.includes(technicalContact.value)) {
      technicalContact.value = null
    }
    if (!availableContactMap.includes(administrativeContact.value)) {
      administrativeContact.value = null
    }
    if (!availableContactMap.includes(billingContact.value)) {
      billingContact.value = null
    }
  },
)

watchEffect(() => {
  if (useOwnerContactForAll.value && ownerContact.value) {
    if (componentProps.product.domainTldSettingInfo?.hasTechContact) {
      technicalContact.value = ownerContact.value
    }
    if (componentProps.product.domainTldSettingInfo?.hasAdminContact) {
      administrativeContact.value = ownerContact.value
    }
    if (componentProps.product.domainTldSettingInfo?.hasBillingContact) {
      billingContact.value = ownerContact.value
    }
  }
})

// Lifecycle hooks
onBeforeMount(async () => {
  pageLoading.value = true
  if (!authStore.hasServicePermission(itemsService, ['find_assigned']) || !authStore.hasServicePermission(itemsService, ['get_assigned'])) {
    await router.push({ name: 'main' })
    return
  }

  await getFilters()

  pageLoading.value = false
})
</script>

<template>
  <q-linear-progress v-if="pageLoading" query class="absolute-top z-top" />
  <div class="row" v-if="!pageLoading">
    <div class="col-12 col-lg-5 flex q-pl-sm">
      <q-card flat class="rounded-borders flex flex-grow-1 full-width" bordered>
        <q-card-section class="full-width q-pa-sm q-pa-sm-md">
          <span class="text-h5 row bordered-header q-mb-md q-pb-sm">{{ $t('Customer.Domain-contacts.Link contacts') }}</span>
          <div class="row q-mt-md">
            <div class="col-12 text-weight-bolder">{{ $t('Customer.Domain-contacts.OwnerContact') }}:</div>
            <div class="col-12 q-mt-sm">
              <omni-select
                :error="!!domainValidationErrors.ownerContact"
                :options="availableContactsOptions"
                v-model="ownerContact"
                class="full-width q-field--error remove-bottom-padding"
              >
                <template v-if="!ownerContact" #selected>
                  <div>{{ $t('Customer.Domain-contacts.Choose a contact') }}</div>
                </template>
                <template v-else #selected-item="{ opt }">
                  <div class="ellipsis" v-if="opt.caption">{{ opt.caption }} - {{ opt.label }}</div>
                  <div class="ellipsis" v-else>{{ opt.label }}</div>
                </template>
              </omni-select>
            </div>
          </div>
          <div class="row q-my-sm">
            <div class="col-auto flex items-center">{{ $t('Customer.Domain-contacts.Set this for all contacts') }}:</div>
            <div class="col flex q-pl-sm">
              <q-toggle v-model="useOwnerContactForAll" name="useOwnerContactForAllToggle" />
            </div>
          </div>
          <div class="row" v-if="product.domainTldSettingInfo?.hasTechContact">
            <div class="col-12 q-mt-sm text-weight-bolder">{{ $t('Customer.Domain-contacts.TechnicalContact') }}:</div>
            <div class="col-12 q-mt-sm">
              <omni-select
                :error="!!domainValidationErrors.technicalContact"
                :options="availableContactsOptions"
                v-model="technicalContact"
                class="q-field--error"
                :readonly="useOwnerContactForAll"
              >
                <template v-if="!technicalContact" #selected>
                  <div>{{ $t('Customer.Domain-contacts.Choose a contact') }}</div>
                </template>
                <template v-else #selected-item="{ opt }">
                  <div class="ellipsis" v-if="opt.caption">{{ opt.caption }} - {{ opt.label }}</div>
                  <div class="ellipsis" v-else>{{ opt.label }}</div>
                </template>
              </omni-select>
            </div>
          </div>

          <div class="row" v-if="product.domainTldSettingInfo?.hasAdminContact">
            <div class="col-12 q-mt-sm text-weight-bolder">{{ $t('Customer.Domain-contacts.AdministrativeContact') }}:</div>
            <div class="col-12 q-mt-sm">
              <omni-select
                :error="!!domainValidationErrors.administrativeContact"
                :options="availableContactsOptions"
                v-model="administrativeContact"
                class="q-field--error"
                :readonly="useOwnerContactForAll"
              >
                <template v-if="!administrativeContact" #selected>
                  <div>{{ $t('Customer.Domain-contacts.Choose a contact') }}</div>
                </template>
                <template v-else #selected-item="{ opt }">
                  <div class="ellipsis" v-if="opt.caption">{{ opt.caption }} - {{ opt.label }}</div>
                  <div class="ellipsis" v-else>{{ opt.label }}</div>
                </template>
              </omni-select>
            </div>
          </div>

          <div class="row" v-if="product.domainTldSettingInfo?.hasBillingContact">
            <div class="col-12 q-mt-sm text-weight-bolder">{{ $t('Customer.Domain-contacts.BillingContact') }}:</div>
            <div class="col-12 q-mt-sm">
              <omni-select
                :error="!!domainValidationErrors.billingContact"
                :options="availableContactsOptions"
                v-model="billingContact"
                class="q-field--error"
                :readonly="useOwnerContactForAll"
              >
                <template v-if="!billingContact" #selected>
                  <div>{{ $t('Customer.Domain-contacts.Choose a contact') }}</div>
                </template>
                <template v-else #selected-item="{ opt }">
                  <div class="ellipsis" v-if="opt.caption">{{ opt.caption }} - {{ opt.label }}</div>
                  <div class="ellipsis" v-else>{{ opt.label }}</div>
                </template>
              </omni-select>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </div>
    <div class="col-12 col-lg-7 flex q-mt-md q-mt-lg-none q-pl-sm q-pl-lg-md">
      <q-card flat class="rounded-borders flex flex-grow-1 full-width q-ml-sm" bordered>
        <q-card-section class="full-width q-pa-sm q-pa-sm-md">
          <span class="text-h5 row bordered-header q-mb-md q-pb-sm">{{ $t('Customer.Domain-contacts.All contacts') }}</span>
          <contacts-page ref="contactsPage" included @update:contacts="getFilters" />
        </q-card-section>
      </q-card>
    </div>
  </div>
</template>

<style scoped></style>
